import React, { useEffect, useState } from "react";
import PasswordInput from "./PasswordInput";
import { SHOP_LOGO_SIZES } from "./shopLogoTokens";
import {
  NORTH_AMERICA_TIME_ZONE_GROUPS,
  TIME_ZONE_VALUES,
  getBrowserTimeZone,
  isValidTimeZone,
  formatNowForTimeZone,
} from "../utils/timezones";
import {
  PHONE_COUNTRY_OPTIONS,
  DEFAULT_PHONE_COUNTRY,
  formatAsYouType,
  validatePhone,
} from "../utils/phone";

import { API_BASE as API } from "../utils/apiBase";
import { SHOP_SERVICE_GROUPS } from "../utils/shopServices";

const VERIFY_CTX_KEY = "verify.context";

const PASSWORD_HELP_TEXT =
  "Password must be at least 12 characters and include uppercase, lowercase, a number, and a special character.";
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const SERVICE_RADIUS_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 250];

function saveVerifyCtx(email, from = "signup") {
  try {
    localStorage.setItem(
      VERIFY_CTX_KEY,
      JSON.stringify({ email, from, ts: Date.now() })
    );
  } catch {}
}

function passwordMeetsRequirements(pw) {
  const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;
  return re.test(pw || "");
}

async function fetchMakesWithFallback() {
  const candidates = [
    `${API}/makes`,
    `${API}/vehicles/makes`,
    `/vehicles/makes`,
    `/makes`,
    `${API}/makes/`,
    `${API}/vehicles/makes/`,
    `/vehicles/makes/`,
    `/makes/`,
  ];

  let lastErr = null;

  for (const url of candidates) {
    try {
      const resp = await fetch(url, { method: "GET" });
      if (!resp.ok) {
        lastErr = new Error(`${url} -> ${resp.status} ${resp.statusText}`);
        continue;
      }

      const data = await resp.json().catch(() => null);

      let raw = data;
      if (raw && Array.isArray(raw.items)) raw = raw.items;

      const makes = Array.isArray(raw)
        ? raw.map((m) => (typeof m === "string" ? m : m?.name)).filter(Boolean)
        : [];

      return makes;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Failed to load makes");
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed reading logo file"));
    reader.readAsDataURL(file);
  });
}

export default function SignupForm({ onGoBack, onGoVerify, initialRole = "" }) {
  // Preselected when the user arrived via a role-specific landing CTA ("Find a Shop" /
  // "Register Your Shop"); still changeable in the form.
  const [role, setRole] = useState(
    initialRole === "shop" || initialRole === "customer" ? initialRole : ""
  ); // "", "shop", or "customer"

  const [shopName, setShopName] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [businessWebsiteUrl, setBusinessWebsiteUrl] = useState("");
  const [businessFacebookUrl, setBusinessFacebookUrl] = useState("");
  const [businessYoutubeUrl, setBusinessYoutubeUrl] = useState("");
  const [businessGoogleProfileUrl, setBusinessGoogleProfileUrl] = useState("");
  const [shopLogoFile, setShopLogoFile] = useState(null);
  const [shopLogoPreview, setShopLogoPreview] = useState("");

  // Time zone selector state
  const [tzMode, setTzMode] = useState("preset"); // "preset" | "custom"
  const [tzPreset, setTzPreset] = useState("UTC");
  const [tzCustom, setTzCustom] = useState("");

  const timeZoneValue =
    tzMode === "preset" ? tzPreset : (tzCustom || "").trim();

  const tzIsValid = tzMode !== "custom" || isValidTimeZone(timeZoneValue);

  const [generalRepair, setGeneralRepair] = useState(true);
  const [mobileMechanic, setMobileMechanic] = useState(false);
  const [mobileRadius, setMobileRadius] = useState(25);
  const [makes, setMakes] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const [makesLoading, setMakesLoading] = useState(false);
  const [makesError, setMakesError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showShopSignupConfirm, setShowShopSignupConfirm] = useState(false);

  // Initialize timezone to browser timezone on first load
  useEffect(() => {
    const tz = (getBrowserTimeZone() || "UTC").trim();
    if (TIME_ZONE_VALUES.includes(tz)) {
      setTzMode("preset");
      setTzPreset(tz);
      setTzCustom("");
    } else {
      setTzMode("custom");
      setTzPreset("UTC");
      setTzCustom(tz);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMakes() {
      setMakesError("");
      setMakesLoading(true);
      try {
        const list = await fetchMakesWithFallback();
        if (!cancelled) setMakes(list);
      } catch (e) {
        if (!cancelled) setMakesError(String(e?.message || e));
      } finally {
        if (!cancelled) setMakesLoading(false);
      }
    }

    if (role === "shop") {
      loadMakes();
    } else {
      setMakes([]);
      setSelectedMakes([]);
      setSelectedServices([]);
      setMakesError("");
      setMakesLoading(false);
      setShopName(""); // clear shop name when switching back to customer
      setBusinessWebsiteUrl("");
      setBusinessFacebookUrl("");
      setBusinessYoutubeUrl("");
      setShopLogoFile(null);
      setShopLogoPreview("");
      setMobileMechanic(false);
      setMobileRadius(25);
    }

    return () => {
      cancelled = true;
    };
  }, [role]);

  const toggleService = (svc) => {
    setSelectedServices((prev) =>
      prev.includes(svc) ? prev.filter((s) => s !== svc) : [...prev, svc]
    );
  };

  const toggleMake = (mk) => {
    setSelectedMakes((prev) =>
      prev.includes(mk) ? prev.filter((m) => m !== mk) : [...prev, mk]
    );
  };

  const handleBack = () => {
    if (typeof onGoBack === "function") return onGoBack();
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/");
  };

  const applyBrowserTimeZone = () => {
    const tz = (getBrowserTimeZone() || "UTC").trim();
    if (TIME_ZONE_VALUES.includes(tz)) {
      setTzMode("preset");
      setTzPreset(tz);
      setTzCustom("");
    } else {
      setTzMode("custom");
      setTzPreset("UTC");
      setTzCustom(tz);
    }
  };

  function validateSignup() {
    if (role === "shop" && !shopName.trim()) {
      setError("Shop name is required for shop accounts.");
      return false;
    }

    if (role !== "shop" && role !== "customer") {
      setError("Please choose an account type before signing up.");
      return false;
    }

    if (role === "shop" && !zip.trim()) {
      setError(
        mobileMechanic
          ? "A home-base ZIP is required for mobile mechanics."
          : "ZIP code is required for shop accounts."
      );
      return false;
    }

    // Mobile mechanics travel to the customer, so a street address isn't required -
    // only a shop name and a home-base ZIP (their service-area center).
    if (role === "shop" && !mobileMechanic && !street.trim()) {
      setError("Street address is required for shop accounts.");
      return false;
    }

    if (role === "shop" && !mobileMechanic && !city.trim()) {
      setError("City is required for shop accounts.");
      return false;
    }

    if (role === "shop" && !mobileMechanic && !state.trim()) {
      setError("State is required for shop accounts.");
      return false;
    }

    if (!first.trim()) {
      setError("First name is required.");
      return false;
    }

    if (!last.trim()) {
      setError("Last name is required.");
      return false;
    }

    if (password !== confirm) {
      setError("Passwords do not match.");
      return false;
    }

    if (!passwordMeetsRequirements(password)) {
      setError(PASSWORD_HELP_TEXT);
      return false;
    }

    if (!tzIsValid) {
      setError(
        "Please enter a valid time zone name (example: America/Chicago) or select one from the list."
      );
      return false;
    }

    if (!validatePhone(phone, phoneCountry).valid) {
      setError("Please enter a valid phone number, or leave it blank.");
      return false;
    }

    return true;
  }

  async function submitSignup() {
    if (submitting) return;
    setSubmitting(true);
    try {
      let shopLogoDataUrl = null;
      if (role === "shop" && shopLogoFile) {
        shopLogoDataUrl = await fileToDataUrl(shopLogoFile);
      }

      const payload = {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim().toLowerCase(),
        phone_number: validatePhone(phone, phoneCountry).e164,
        street_address: street.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        zipcode: zip.trim() || null,

        time_zone: (timeZoneValue || "UTC").trim() || "UTC",

        is_shop: role === "shop",
        shop_name: role === "shop" ? (shopName.trim() || null) : null,
        shop_general_repair: role === "shop" ? !!generalRepair : false,
        shop_specializations: role === "shop" ? selectedMakes : null,
        shop_services: role === "shop" ? selectedServices : null,
        is_mobile_mechanic: role === "shop" ? !!mobileMechanic : false,
        mobile_service_radius_miles:
          role === "shop" && mobileMechanic ? Number(mobileRadius) || 25 : null,
        business_website_url:
          role === "shop" ? (businessWebsiteUrl.trim() || null) : null,
        business_facebook_url:
          role === "shop" ? (businessFacebookUrl.trim() || null) : null,
        business_youtube_url:
          role === "shop" ? (businessYoutubeUrl.trim() || null) : null,
        business_google_profile_url:
          role === "shop" ? (businessGoogleProfileUrl.trim() || null) : null,
        shop_logo_data_url: role === "shop" ? shopLogoDataUrl : null,

        password,
      };

      const res = await fetch(`${API}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Signup failed");
      }

      setStatus("Signup successful - check your email for the verification code.");
      saveVerifyCtx(payload.email, "signup");

      if (typeof onGoVerify === "function") {
        onGoVerify(payload.email);
      } else {
        window.location.assign(
          `/verify?email=${encodeURIComponent(payload.email)}&from=signup`
        );
      }
    } catch (err) {
      setError(err?.message || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setStatus("");

    if (!validateSignup()) return;

    if (role === "shop") {
      setShowShopSignupConfirm(true);
      return;
    }

    await submitSignup();
  }

  async function confirmShopSignup() {
    setShowShopSignupConfirm(false);
    setError("");
    setStatus("");
    await submitSignup();
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-3 p-4">
      <h1 className="text-2xl font-semibold">Create your GearCheckAuto account</h1>

      <div className="space-y-2">
        <div className="text-sm font-medium">Account type</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setRole("customer")}
            className={`text-left border rounded-xl p-3 transition ${
              role === "customer"
                ? "border-blue-600 ring-2 ring-blue-200 bg-blue-50 dark:bg-blue-950/40"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
            }`}
            aria-pressed={role === "customer"}
          >
            <div className="font-medium">Customer</div>
            <div className="text-xs opacity-75">Requesting services</div>
          </button>
          <button
            type="button"
            onClick={() => setRole("shop")}
            className={`text-left border rounded-xl p-3 transition ${
              role === "shop"
                ? "border-blue-600 ring-2 ring-blue-200 bg-blue-50 dark:bg-blue-950/40"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
            }`}
            aria-pressed={role === "shop"}
          >
            <div className="font-medium">Shop</div>
            <div className="text-xs opacity-75">Providing services</div>
          </button>
        </div>
        {!role && (
          <div className="text-xs text-amber-700 dark:text-amber-300">
            Please pick one account type to continue.
          </div>
        )}
      </div>

      {/* ✅ Shop name moved ABOVE first/last */}
      {role === "shop" && (
        <>
          <input
            className="w-full border p-2 rounded"
            placeholder="Shop name"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            required
          />
          <div className="border rounded-xl p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Shop logo (optional)</div>
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">PNG, JPG, or WEBP. Max 5MB. Large images are auto-resized.</div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="mt-2 block w-full text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (!file) {
                  setShopLogoFile(null);
                  setShopLogoPreview("");
                  return;
                }
                if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
                  setError("Logo must be PNG, JPG, or WEBP.");
                  e.target.value = "";
                  return;
                }
                if (file.size > MAX_LOGO_BYTES) {
                  setError("Logo file exceeds 5MB.");
                  e.target.value = "";
                  return;
                }
                setError("");
                setShopLogoFile(file);
                setShopLogoPreview(URL.createObjectURL(file));
              }}
            />
            {shopLogoPreview ? (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={shopLogoPreview}
                  alt="Shop logo preview"
                  className="rounded border object-contain bg-white dark:bg-gray-800"
                  style={{ width: SHOP_LOGO_SIZES.signupPreview, height: SHOP_LOGO_SIZES.signupPreview }}
                />
                <button
                  type="button"
                  className="text-sm px-2 py-1 border rounded"
                  onClick={() => {
                    setShopLogoFile(null);
                    setShopLogoPreview("");
                  }}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input
          className="w-full border p-2 rounded"
          placeholder="First name"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          required
          autoComplete="given-name"
        />
        <input
          className="w-full border p-2 rounded"
          placeholder="Last name"
          value={last}
          onChange={(e) => setLast(e.target.value)}
          required
          autoComplete="family-name"
        />
      </div>

      <input
        className="w-full border p-2 rounded"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        required
        autoComplete="email"
      />

      <div className="flex gap-2">
        <select
          className="border p-2 rounded max-w-[9rem]"
          value={phoneCountry}
          onChange={(e) => setPhoneCountry(e.target.value)}
          aria-label="Phone country"
        >
          {PHONE_COUNTRY_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>{opt.label}</option>
          ))}
        </select>
        <input
          className="w-full border p-2 rounded"
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(formatAsYouType(e.target.value, phoneCountry))}
          type="tel"
          autoComplete="tel"
        />
      </div>
      {phone && !validatePhone(phone, phoneCountry).valid ? (
        <p className="text-xs text-red-700">
          That phone number doesn&rsquo;t look valid for the selected country.
        </p>
      ) : null}

      {role === "shop" && (
        <div className="border rounded-xl p-3 space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={mobileMechanic}
              onChange={(e) => setMobileMechanic(e.target.checked)}
            />
            <span className="font-medium">I'm a mobile mechanic (I travel to customers)</span>
          </label>
          <div className="text-xs opacity-75">
            Mobile mechanics appear on the map as a general service area around their ZIP - not a
            street address. You only need a home-base ZIP, plus how far you'll travel.
          </div>
          {mobileMechanic && (
            <label className="block text-sm font-medium">
              Service radius
              <select
                className="mt-1 w-full border p-2 rounded bg-white dark:bg-gray-800"
                value={mobileRadius}
                onChange={(e) => setMobileRadius(Number(e.target.value))}
              >
                {SERVICE_RADIUS_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r} miles
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      <input
        className="w-full border p-2 rounded"
        placeholder={mobileMechanic ? "Street address (optional)" : "Street address"}
        value={street}
        onChange={(e) => setStreet(e.target.value)}
        autoComplete="street-address"
        required={role === "shop" && !mobileMechanic}
      />

      <div className="grid grid-cols-3 gap-2">
        <input
          className="w-full border p-2 rounded"
          placeholder={mobileMechanic ? "City (optional)" : "City"}
          value={city}
          onChange={(e) => setCity(e.target.value)}
          autoComplete="address-level2"
          required={role === "shop" && !mobileMechanic}
        />
        <input
          className="w-full border p-2 rounded"
          placeholder={mobileMechanic ? "State (optional)" : "State"}
          value={state}
          onChange={(e) => setState(e.target.value)}
          autoComplete="address-level1"
          required={role === "shop" && !mobileMechanic}
        />
        <input
          className="w-full border p-2 rounded"
          placeholder={mobileMechanic ? "Home ZIP" : "ZIP"}
          value={zip}
          inputMode="numeric"
          maxLength={5}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          autoComplete="postal-code"
          aria-label={mobileMechanic ? "Home ZIP code" : "ZIP code"}
          required={role === "shop"}
        />
      </div>

      {/* Time zone section */}
      <div className="border rounded p-3">
        <div className="font-medium">Time zone</div>
        <div className="text-xs opacity-75 mb-2">
          Used to display message timestamps.
        </div>

        <select
          className="w-full border p-2 rounded bg-white dark:bg-gray-800"
          value={tzMode === "preset" ? tzPreset : "__custom__"}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") {
              setTzMode("custom");
            } else {
              setTzMode("preset");
              setTzPreset(v);
              setTzCustom("");
            }
          }}
        >
          {NORTH_AMERICA_TIME_ZONE_GROUPS.map((group) => (
            <optgroup key={group.region} label={group.region}>
              {group.zones.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label}
                </option>
              ))}
            </optgroup>
          ))}
          <option value="__custom__">Custom…</option>
        </select>

        {tzMode === "custom" && (
          <div className="mt-2">
            <input
              className="w-full border p-2 rounded"
              placeholder="e.g. America/Chicago"
              value={tzCustom}
              onChange={(e) => setTzCustom(e.target.value)}
            />
            {!tzIsValid && (
              <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                That time zone doesn’t look valid. Example:{" "}
                <span className="font-mono">America/Chicago</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={applyBrowserTimeZone}
            className="px-3 py-2 border rounded-xl"
          >
            Use browser time zone
          </button>

          <div className="text-xs opacity-75">
            Preview: {formatNowForTimeZone(timeZoneValue || "UTC")}
          </div>
        </div>
      </div>

      {/* Shop settings (general repair + makes) remain below time zone */}
      {role === "shop" && (
        <div className="space-y-3">
          <div className="border rounded p-3 space-y-2">
            <div className="font-medium">Business links (optional)</div>
            <input
              className="w-full border p-2 rounded"
              placeholder="Website URL"
              value={businessWebsiteUrl}
              onChange={(e) => setBusinessWebsiteUrl(e.target.value)}
              type="text"
              autoComplete="url"
            />
            <input
              className="w-full border p-2 rounded"
              placeholder="Facebook URL"
              value={businessFacebookUrl}
              onChange={(e) => setBusinessFacebookUrl(e.target.value)}
              type="text"
              autoComplete="url"
            />
            <input
              className="w-full border p-2 rounded"
              placeholder="YouTube URL"
              value={businessYoutubeUrl}
              onChange={(e) => setBusinessYoutubeUrl(e.target.value)}
              type="text"
              autoComplete="url"
            />
            <input
              className="w-full border p-2 rounded"
              placeholder="Google Business Profile URL (Share > Copy link)"
              value={businessGoogleProfileUrl}
              onChange={(e) => setBusinessGoogleProfileUrl(e.target.value)}
              type="text"
              autoComplete="url"
            />
            <p className="text-xs text-gray-500">
              Powers your star rating. To get the exact link: on Google Maps, open your business, tap Share, then Copy link.
            </p>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={generalRepair}
              onChange={(e) => setGeneralRepair(e.target.checked)}
            />
            General auto repair (all makes)
          </label>

          <div>
            <div className="font-medium mb-1">
              Select Specializations (optional):
            </div>

            {makesLoading && (
              <div className="text-sm opacity-75">Loading makes…</div>
            )}

            {makesError && (
              <div className="text-sm text-red-600 dark:text-red-400">
                Failed to load makes: {makesError}
              </div>
            )}

            {!makesLoading && !makesError && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-56 overflow-y-auto border rounded p-2">
                {makes.map((mk) => (
                  <label key={mk} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedMakes.includes(mk)}
                      onChange={() => toggleMake(mk)}
                    />
                    {mk}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="font-medium mb-1">
              Select services offered (optional):
            </div>
            <div className="max-h-56 overflow-y-auto border rounded p-2 space-y-2">
              {SHOP_SERVICE_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className="text-xs font-semibold uppercase opacity-60 mb-1">
                    {group.label}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {group.services.map((svc) => (
                      <label key={svc} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(svc)}
                          onChange={() => toggleService(svc)}
                        />
                        {svc}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border rounded p-3 space-y-2">
        <div className="text-xs opacity-80">{PASSWORD_HELP_TEXT}</div>

        <PasswordInput
          className="w-full border p-2 rounded"
          placeholder="Password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />

        <PasswordInput
          className="w-full border p-2 rounded"
          placeholder="Confirm password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      {error && <div className="text-red-600 dark:text-red-400">{error}</div>}
      {status && <div className="text-green-600 dark:text-green-400">{status}</div>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleBack}
          className="px-3 py-2 border rounded-xl inline-flex items-center justify-center gap-2"
          title="Back"
          aria-label="Back"
        >
          <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
        </button>
        <button
          type="submit"
          className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={!tzIsValid || !role || submitting}
          title={
            !tzIsValid
              ? "Please enter a valid time zone"
              : !role
              ? "Please choose an account type"
              : ""
          }
        >
          {submitting ? "Creating account..." : "Sign Up"}
        </button>
      </div>

      {showShopSignupConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Sign up as a shop account?</p>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              Shop accounts are for businesses providing services. Continue creating this shop account?
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={confirmShopSignup}
                disabled={submitting}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="fi fi-rr-check not-italic" />
                {submitting ? "Creating account..." : "Yes"}
              </button>
              <button
                type="button"
                onClick={() => setShowShopSignupConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
